import { mapAllCompositions, mapCce, mapDl, mapViscosity, mapCvd, FLUID_TYPES } from "./mappers";
import { TEST_FEATURES } from "./categories";
import { EMPTY_FRAME, isEmptyFrame, hasColumn, dropDuplicates, leftMergeUnique, filterRows } from "./frame";

export function buildEdaDatasets(samples, metadataTables) {
  const list = Array.isArray(samples) ? samples : [];
  const attributes = sampleAttributes(list);
  return {
    composition: mapAllCompositions(list, metadataTables, true),
    compositionUnfiltered: mapAllCompositions(list, metadataTables, false),
    cce: withSampleAttributes(mapCce(list, metadataTables), attributes),
    dl: withSampleAttributes(mapDl(list, metadataTables), attributes),
    viscosity: withSampleAttributes(mapViscosity(list, metadataTables), attributes),
    cvd: withSampleAttributes(mapCvd(list, metadataTables), attributes),
    sampleCount: list.length,
  };
}

const SAMPLE_ATTRIBUTES = [
  ["Fluid Classification", "fluidClassification"],
  ["Sample Type", "sampleType"],
];

function sampleAttributes(samples) {
  const byId = new Map();
  for (const sample of samples) {
    if (!sample || byId.has(sample.ID)) continue;
    byId.set(sample.ID, Object.fromEntries(SAMPLE_ATTRIBUTES.map(([column, key]) => [column, sample[key] ?? ""])));
  }
  return byId;
}

function withSampleAttributes(frame, attributes) {
  if (isEmptyFrame(frame)) return frame;
  const added = SAMPLE_ATTRIBUTES.map(([column]) => column).filter((c) => !frame.columns.includes(c));
  return {
    columns: [...frame.columns, ...added],
    rows: frame.rows.map((row) => ({ ...row, ...(attributes.get(row.ID) || {}) })),
  };
}

function compositionFrame(compositionTables, phase, dataType) {
  if (dataType !== "mole" && dataType !== "wt") return EMPTY_FRAME;
  const byPhase = compositionTables[phase] || compositionTables["Reservoir Fluid"];
  return (byPhase && byPhase[dataType]) || EMPTY_FRAME;
}

function normaliseFluidTypes(fluidTypes) {
  if (fluidTypes == null) return [];
  return Array.isArray(fluidTypes) ? fluidTypes : [fluidTypes];
}

export function filterByFluidTypes(frame, fluidTypes) {
  const types = normaliseFluidTypes(fluidTypes);
  if (types.length === 0 || !hasColumn(frame, "Sample Kind")) return frame;
  const set = new Set(types);
  return filterRows(frame, (row) => set.has(row["Sample Kind"]));
}

const TEST_MERGES = [
  ["cce", "_cce"],
  ["dl", "_dl"],
  ["viscosity", "_visc"],
  ["cvd", "_cvd"],
];

export function getMergedFrame(datasets, fluidTypes, phase, dataType) {
  if (!datasets) return EMPTY_FRAME;
  let frame = compositionFrame(datasets.composition, phase, dataType);
  if (isEmptyFrame(frame)) return EMPTY_FRAME;

  const keys = ["ID", "Source"];
  for (const [name, suffix] of TEST_MERGES) {
    const test = datasets[name];
    if (isEmptyFrame(test) || !keys.every((k) => hasColumn(test, k))) continue;
    if (!keys.every((k) => hasColumn(frame, k))) continue;
    frame = leftMergeUnique(frame, dropDuplicates(test, keys), keys, suffix);
  }
  return filterByFluidTypes(frame, fluidTypes);
}

export function getCompositionFrame(datasets, fluidTypes, phase, dataType, { unfiltered = false } = {}) {
  if (!datasets) return EMPTY_FRAME;
  const tables = unfiltered ? datasets.compositionUnfiltered : datasets.composition;
  const frame = compositionFrame(tables, phase, dataType);
  if (isEmptyFrame(frame)) return EMPTY_FRAME;
  return filterByFluidTypes(frame, fluidTypes);
}

export function getTestFrame(datasets, testName, fluidTypes) {
  const frame = datasets && datasets[testName];
  if (isEmptyFrame(frame)) return EMPTY_FRAME;
  return filterByFluidTypes(frame, fluidTypes);
}

export function fluidTypesOrAll(fluidTypes) {
  const types = normaliseFluidTypes(fluidTypes);
  return types.length ? types : FLUID_TYPES;
}

const TEST_SOURCES = [
  ["CCE Test Analysis", "cce"],
  ["DL Test Analysis", "dl"],
  ["Viscosity Test Analysis", "viscosity"],
  ["CVD Test Analysis", "cvd"],
];

export function featureSource(feature) {
  for (const [category, source] of TEST_SOURCES) {
    if (TEST_FEATURES[category].includes(feature)) return source;
  }
  return "composition";
}

export function getFeatureFrame(datasets, feature, fluidTypes, phase, dataType) {
  const source = featureSource(feature);
  if (source === "composition") return getCompositionFrame(datasets, fluidTypes, phase, dataType, { unfiltered: true });
  return getTestFrame(datasets, source, fluidTypes);
}
